import type { Editor } from '@tiptap/core';

export const buildInlineStyle = (attributes: { marginLeft?: string; paddingLeft?: string; textIndent?: string }) => {
  const styles = [
    attributes.marginLeft ? `margin-left: ${attributes.marginLeft}` : null,
    attributes.paddingLeft ? `padding-left: ${attributes.paddingLeft}` : null,
    attributes.textIndent ? `text-indent: ${attributes.textIndent}` : null
  ].filter(Boolean);

  return styles.length ? { style: styles.join('; ') } : {};
};

const parseIndentValue = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const numericValue = Number.parseInt(value, 10);

  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const toIndentValue = (value: number) => {
  return value > 0 ? `${value}px` : null;
};

const getCurrentIndentValue = (attributes: {
  marginLeft?: string | null;
  paddingLeft?: string | null;
  textIndent?: string | null;
}) => {
  return (
    parseIndentValue(attributes.marginLeft) ||
    parseIndentValue(attributes.paddingLeft) ||
    parseIndentValue(attributes.textIndent)
  );
};


export const getClipboardImageFile = (event: ClipboardEvent) => {
  const files = Array.from(event.clipboardData?.files || []);
  const imageFile = files.find(({ type }) => type.startsWith('image/'));

  if (imageFile) {
    return imageFile;
  }

  const items = Array.from(event.clipboardData?.items || []);
  const imageItem = items.find(({ kind, type }) => kind === 'file' && type.startsWith('image/'));

  return imageItem?.getAsFile() || null;
};

export const insertImageFileAsBase64 = (editor: Editor | null, file: File, onLoad?: () => void) => {
  if (!editor) {
    return;
  }

  const reader = new FileReader();

  reader.onload = () => {
    const result = reader.result;

    if (typeof result !== 'string') {
      return;
    }

    editor.chain().focus().setImage({ src: result, alt: file.name }).run();
  };

  reader.onloadend = () => {
    onLoad?.();
  };

  reader.readAsDataURL(file);
};

export const updateSelectedNodeIndentation = (
  editor: Editor | null,
  direction: 'indent' | 'outdent',
  indentStep = 40
) => {
  if (!editor) {
    return;
  }

  const nodeType = editor.state.selection.$from.parent.type.name;

  if (!['paragraph', 'heading', 'listItem', 'bulletList', 'orderedList'].includes(nodeType)) {
    return;
  }

  const currentAttributes = editor.getAttributes(nodeType);
  const nextMarginLeft = toIndentValue(
    Math.max(
      0,
      getCurrentIndentValue(currentAttributes) + (direction === 'indent' ? indentStep : -indentStep)
    )
  );

  editor.commands.updateAttributes(nodeType, {
    ...currentAttributes,
    marginLeft: nextMarginLeft,
    paddingLeft: null,
    textIndent: null
  });
};
